import { onMounted, onUnmounted, ref, toValue } from "vue";
import type { UseLazyRevealOptions, UseLazyRevealReturn } from "./types";

const DEFAULT_ROOT_MARGIN = "200px";

/**
 * Lazy-reveal an element when it enters the viewport.
 *
 * Wraps a single-shot IntersectionObserver. Once the element intersects,
 * `isVisible` flips to `true` and the observer disconnects. Call `reattach`
 * after resetting `isVisible` (e.g., when `src` changes) to observe again.
 *
 * When `enabled` is falsy, `isVisible` is set to `true` immediately and no
 * observer is created.
 *
 * `enabled` is sampled once at `onMounted` and on each `reattach()` — if a
 * consumer flips it at runtime, they must call `reattach()` explicitly.
 */
export const useLazyReveal = (options: UseLazyRevealOptions): UseLazyRevealReturn => {
  const { elementRef, enabled = true, rootMargin = DEFAULT_ROOT_MARGIN } = options;
  const isVisible = ref(false);
  let observer: IntersectionObserver | null = null;

  const reattach = (): void => {
    observer?.disconnect();
    observer = null;
    const el = toValue(elementRef);
    if (!el || !toValue(enabled)) {
      isVisible.value = true;
      return;
    }
    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          isVisible.value = true;
          observer?.disconnect();
          observer = null;
        }
      },
      { rootMargin },
    );
    observer.observe(el);
  };

  onMounted(reattach);
  onUnmounted(() => {
    observer?.disconnect();
    observer = null;
  });

  return { isVisible, reattach };
};
